<template>
  <div class="radio-bar-input">
    <div
      v-for="option in options"
      :key="option.name"
      :class="inputClass(option)"
      class="radio-input"
      @click="handleClick"
    >
      <input
        :name="name"
        :value="option.value"
        class="radio-input__input"
        type="radio"
        @change="handleInputValue"
      >
      <font-awesome-icon
        :icon="option.icon"
        class="radio-input__icon"
      />
    </div>
  </div>
</template>

<script>
import isBlank from 'is-blank'
import unique from 'just-unique'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

function validateOptionAttributes (options) {
  const completeOptions = options.filter(({ isWarning, icon, value }) => {
    return !isBlank(icon) && !isBlank(value)
  })
  if (completeOptions.length === options.length) {
    return true
  } else {
    throw new Error('[RadioBarInput] Required props: `icon` and `value`. Optional: `isWarning`.')
  }
}

function validateUniqueValues (options) {
  const uniqueValues = unique(options.map(o => o.value))
  if (uniqueValues.length === options.length) {
    return true
  } else {
    throw new Error('[RadioBarInput] values have to be unique.')
  }
}

export default {
  name: 'RadioBarInput',
  components: {
    FontAwesomeIcon
  },
  props: {
    name: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      required: true,
      validator (options) {
        return validateOptionAttributes(options) && validateUniqueValues(options)
      }
    },
    value: {
      type: String,
      required: true
    }
  },
  computed: {
    isSelected () {
      return option => option.value === this.value
    },
    inputClass () {
      return option => ({
        'radio-input--selected': this.isSelected(option),
        'radio-input--warning': option.isWarning
      })
    }
  },
  methods: {
    handleClick (event) {
      const input = event.target.querySelector('input')
      if (input) {
        input.click()
      }
    },
    handleInputValue (event) {
      this.$emit('input', event.target.value)
      this.$emit('change', event.target.value)
    }
  }
}
</script>

<style lang="sass">
@import '~styles/imports'

.radio-input
  +font-size(14, 40)
  background: #f0f3f5
  border-radius: 0
  border: 2px solid transparent
  color: #000
  cursor: pointer
  display: inline-block
  font-family: $font-1
  font-weight: 600
  height: rem-calc(42)
  overflow: hidden
  padding: 0
  position: relative
  text-align: center
  text-overflow: ellipsis
  transition: $global-transition
  white-space: nowrap
  width: rem-calc(50)

  &:hover,
  &:focus
    background: lighten($color-grey-1, 3%)
    outline: 0 none
    text-decoration: none

  &:active
    box-shadow: inset $shadow-4dp

  .fa
    margin-right: 0

  &:first-child
    border-radius: 3px 0 0 3px

  &:last-child
    border-radius: 0 3px 3px 0

  & + &
    border-left: 1px solid $color-grey-2

.radio-input--selected
  background: #282d37
  color: #fff

  &:hover,
  &:focus
    background: #282d37

  &.radio-input--warning
    background: $color-error
    color: #fff

    &:hover,
    &:focus
      background: $color-error

.radio-input--disabled
  background: $color-grey-1
  color: $color-white

  &:hover,
  &:focus
    background: $color-grey-2

.radio-input__input
  +visuallyhidden

.radio-input__icon
  pointer-events: none

</style>



// WEBPACK FOOTER //
// src/components/forms/RadioBarInput.vue